import { z } from "zod";

import {
  AccountSchema,
  ActivationStatus,
  CreditSchema,
  OrgContactSchema,
  OrgMemberRole,
  OrgMemberSchema,
  OrgReviewSchema,
  OrgSchema,
  OrgStatsSchema,
  ReferralStatsSchema,
  UserSchema
} from "../../models";

export { OrgMemberRole };
export * from "./invitation";

export const OrgRatingDistributionSchema = z
  .object({
    "1": z.number().int().min(0),
    "2": z.number().int().min(0),
    "3": z.number().int().min(0),
    "4": z.number().int().min(0),
    "5": z.number().int().min(0)
  })
  .default({
    "1": 0,
    "2": 0,
    "3": 0,
    "4": 0,
    "5": 0
  });

export const OrgPublicDetailSchema = OrgSchema.extend({
  stats: OrgStatsSchema.extend({
    ratingDistribution: OrgRatingDistributionSchema
  }),
  contact: OrgContactSchema
});

export type OrgPublicDetail = z.infer<typeof OrgPublicDetailSchema>;

export const OrgReviewDetailSchema = OrgReviewSchema.extend({
  user: UserSchema
});

export type OrgReviewDetail = z.infer<typeof OrgReviewDetailSchema>;

export const CreateOrgReviewSchema = OrgReviewSchema.omit({
  id: true,
  userId: true,
  createdAt: true,
  updatedAt: true
});

export type CreateOrgReview = z.infer<typeof CreateOrgReviewSchema>;

export const OrgMemberDetailSchema = OrgMemberSchema.extend({
  org: OrgSchema.extend({
    stats: OrgStatsSchema
  })
});

export type OrgMemberDetail = z.infer<typeof OrgMemberDetailSchema>;

export const OrgMemberWithUserSchema = OrgMemberSchema.extend({
  user: UserSchema
});

export type OrgMemberWithUser = z.infer<typeof OrgMemberWithUserSchema>;

export const OrgFullDetailSchema = OrgPublicDetailSchema.extend({
  stats: OrgStatsSchema.extend({
    ratingDistribution: OrgRatingDistributionSchema
  }),
  account: AccountSchema.extend({
    stats: ReferralStatsSchema,
    credit: CreditSchema
  }),
  contact: OrgContactSchema,
  members: z.array(OrgMemberWithUserSchema)
});

export type OrgFullDetail = z.infer<typeof OrgFullDetailSchema>;

export const CreateOrgSchema = OrgSchema.pick({
  avatar: true,
  cover: true,
  name: true,
  tagline: true,
  description: true,
  mission: true
}).extend({
  contact: OrgContactSchema.partial().omit({
    orgId: true,
    createdAt: true,
    updatedAt: true
  })
});
export type CreateOrg = z.infer<typeof CreateOrgSchema>;

export const UpdateOrgSchema = z.object({
  name: z.string().min(1).optional(),
  tagline: z.string().optional(),
  description: z.string().optional(),
  mission: z.string().optional(),
  avatar: z.string().url().optional(),
  cover: z.string().url().optional(),
  contact: z
    .object({
      email: z.string().email().nullable().optional(),
      phone: z.string().nullable().optional(),
      website: z.string().nullable().optional(),
      instagram: z.string().url().nullable().optional(),
      facebook: z.string().url().nullable().optional(),
      twitter: z.string().url().nullable().optional(),
      youtube: z.string().url().nullable().optional()
    })
    .optional()
});
export type UpdateOrg = z.infer<typeof UpdateOrgSchema>;

export const OrgActivationStatusSchema = z.object({
  status: z.nativeEnum(ActivationStatus),
  plan: z.string().nullable()
});
export type OrgActivationStatus = z.infer<typeof OrgActivationStatusSchema>;

export const ActivateOrgSchema = z.object({
  plan: z.string()
});
export type ActivateOrg = z.infer<typeof ActivateOrgSchema>;
